import { http } from '@/shared/lib/api';

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export type TicketCategory = 'technical' | 'question' | 'feedback' | 'other';
export type TicketPriority = 'normal' | 'urgent';
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

export interface AdminTicketTenant {
  id: string;
  company_name: string;
}

export interface AdminTicketUser {
  id: string;
  email: string;
}

export interface AdminTicketSummary {
  id: string;
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  unread_by_admin: boolean;
  created_at: string;
  updated_at: string;
  tenant: AdminTicketTenant;
  user: AdminTicketUser;
}

export interface AdminTicketReply {
  id: string;
  body: string;
  is_admin_reply: boolean;
  author_email: string | null;
  created_at: string;
}

export interface AdminTicketDetail {
  ticket: {
    id: string;
    subject: string;
    category: TicketCategory;
    description: string;
    priority: TicketPriority;
    status: TicketStatus;
    unread_by_admin: boolean;
    unread_by_tenant: boolean;
    created_at: string;
    updated_at: string;
    tenant: AdminTicketTenant;
    user: AdminTicketUser;
  };
  replies: AdminTicketReply[];
}

export interface TicketActivityGroup {
  event_type: string;
  count: number;
  first_at: string;
  last_at: string;
  repeated: boolean;
}

export interface TicketActivitySummary {
  window_start: string;
  window_end: string;
  ticket_created_at: string;
  total_events: number;
  groups: TicketActivityGroup[];
}

export interface AdminListTicketsParams {
  page?: number;
  limit?: number;
  status?: TicketStatus;
  priority?: TicketPriority;
  tenantId?: string;
}

export interface AdminListTicketsResponse {
  items: AdminTicketSummary[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const adminSupportApi = {
  getUnreadCount: () =>
    http
      .get<{ success: true; data: { count: number } }>('/api/admin/support/tickets/unread-count')
      .then((r) => r.data),

  listTickets: (params?: AdminListTicketsParams) =>
    http
      .get<{ success: true; data: AdminListTicketsResponse }>('/api/admin/support/tickets', {
        query: params as Record<string, string | number | boolean | undefined | null>,
      })
      .then((r) => r.data),

  getTicket: (id: string) =>
    http
      .get<{ success: true; data: AdminTicketDetail }>(`/api/admin/support/tickets/${id}`)
      .then((r) => r.data),

  postReply: (ticketId: string, body: string) =>
    http
      .post<{
        success: true;
        data: { id: string; ticket_id: string; body: string; is_admin_reply: boolean; created_at: string };
      }>(`/api/admin/support/tickets/${ticketId}/reply`, { body })
      .then((r) => r.data),

  patchStatus: (ticketId: string, status: TicketStatus) =>
    http
      .patch<{ success: true; data: { id: string; status: TicketStatus } }>(
        `/api/admin/support/tickets/${ticketId}/status`,
        { status },
      )
      .then((r) => r.data),

  getActivitySummary: (ticketId: string, minutes: 15 | 60 | 1440) =>
    http
      .get<{ success: true; data: TicketActivitySummary }>(
        `/api/admin/support/tickets/${ticketId}/activity-summary`,
        { query: { minutes } },
      )
      .then((r) => r.data),
};
